import { FormEvent, useMemo, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal'
import { requestJson } from '@/lib/http'

type CaseRecord = {
  id: string
  legal_name: string
  state_code: string
  entity_type_code: string
  filing_type: string
  lifecycle_state: string
  readiness_errors: string[]
  interview_payload: Record<string, unknown>
}

type PartyProfile = {
  id: string
  profile_type: 'person' | 'entity'
  full_name?: string
  legal_name?: string
  display_name: string
}

type PartyRecord = {
  id: string
  role_name: string
  signer: boolean
  appointed_on?: string | null
  party_profile: PartyProfile
}

type OwnershipEdge = {
  id: string
  owner_party_profile_id: string
  subject_party_profile_id: string
  percentage_owned?: number
  control_type?: string
}

type PartyDocument = {
  id: string
  party_profile_id: string
  party_name: string
  document_type: string
  verification_status: string
  reviewed_at?: string
  rejection_reason?: string
  file_count: number
}

type EinApplication = {
  id: string
  responsible_party_name?: string
  responsible_party_title?: string
  responsible_party_tax_id_masked?: string
  responsible_party_birth_date?: string
  responsible_party_phone?: string
  legal_structure?: string
  reason_for_applying?: string
  answers?: Record<string, unknown>
  complete: boolean
  submitted_at?: string
} | null

type Signature = {
  id: string
  signer_party_profile_id: string
  signer_name: string
  signed_at: string
}

type SignaturePacket = {
  id: string
  status: string
  consent_version: string
  completed_at?: string
  signatures: Signature[]
}

type PaymentLineItem = {
  id: string
  line_item_type: string
  description: string
  amount_cents: number
  tax_amount_cents: number
  currency: string
  jurisdiction_code?: string
}

type PaymentOrder = {
  id: string
  status: string
  currency: string
  subtotal_cents: number
  tax_cents: number
  total_cents: number
  line_items: PaymentLineItem[]
} | null

type PacketVersion = {
  id: string
  status: string
  revision_checksum: string
  currency: string
  total_cents: number
  generated_at: string
  packet_pdf_attached: boolean
}

type FilingReceipt = {
  id: string
  submitted_at: string
  jurisdiction_reference?: string
  notes?: string
  receipt_document_attached: boolean
} | null

type RemittanceTask = {
  id: string
  status: string
  jurisdiction_code: string
  amount_cents: number
  currency: string
  reference_number?: string
  remitted_at?: string
  failure_reason?: string
}

type Props = {
  case_record: CaseRecord
  section_schema: Record<string, { title?: string; fields?: Array<{ key: string; label_legal: string; label_alias?: string; required?: boolean }> }>
  party_profiles: PartyProfile[]
  parties: PartyRecord[]
  ownership_edges: OwnershipEdge[]
  party_documents: PartyDocument[]
  ein_application: EinApplication
  signature_packets: SignaturePacket[]
  payment_order: PaymentOrder
  packet_versions: PacketVersion[]
  filing_receipt: FilingReceipt
  remittance_tasks: RemittanceTask[]
}

function cents(value: number) {
  return (value / 100).toFixed(2)
}

export default function BusinessRegistrationUsCasesShow(props: Props) {
  const {
    case_record,
    section_schema,
    party_profiles,
    parties,
    ownership_edges,
    party_documents,
    ein_application,
    signature_packets,
    payment_order,
    packet_versions,
    filing_receipt,
    remittance_tasks,
  } = props

  const page = usePage()
  const accountSlug = (page.props as { auth?: { account?: { slug?: string } } }).auth?.account?.slug

  const [message, setMessage] = useState<string>('')
  const [error, setError] = useState<string>('')

  const casePath = `/${accountSlug}/business-registration/us/cases/${case_record.id}`

  const [caseForm, setCaseForm] = useState({
    state_code: case_record.state_code,
    entity_type_code: case_record.entity_type_code,
    filing_type: case_record.filing_type,
    legal_name: case_record.legal_name,
    assumed_name: '',
    interview_payload: case_record.interview_payload,
  })

  const sectionKeys = Object.keys(section_schema)
  const [selectedSection, setSelectedSection] = useState(sectionKeys[0] || 'entity_basics')
  const [sectionJson, setSectionJson] = useState(
    JSON.stringify(case_record.interview_payload[selectedSection] || {}, null, 2)
  )

  const [partyForm, setPartyForm] = useState({
    role_name: '',
    signer: false,
    appointed_on: '',
    party_profile_id: '',
    profile_type: 'person',
    full_name: '',
    legal_name: '',
  })

  const [ownershipForm, setOwnershipForm] = useState({
    owner_party_profile_id: '',
    subject_party_profile_id: '',
    percentage_owned: '',
    control_type: '',
  })

  const [documentForm, setDocumentForm] = useState({
    party_profile_id: '',
    document_type: 'passport',
    retention_until: '',
  })
  const [documentFiles, setDocumentFiles] = useState<FileList | null>(null)

  const [einForm, setEinForm] = useState({
    responsible_party_name: ein_application?.responsible_party_name || '',
    responsible_party_title: ein_application?.responsible_party_title || '',
    responsible_party_tax_id: '',
    responsible_party_birth_date: ein_application?.responsible_party_birth_date || '',
    responsible_party_phone: ein_application?.responsible_party_phone || '',
    legal_structure: ein_application?.legal_structure || case_record.entity_type_code,
    reason_for_applying: ein_application?.reason_for_applying || '',
  })

  const [paymentForm] = useState({
    currency: payment_order?.currency || 'USD',
    service_fee_cents: '25000',
    filing_fee_cents: '30000',
  })

  const [filingForm, setFilingForm] = useState({
    submitted_at: filing_receipt?.submitted_at || new Date().toISOString().slice(0, 16),
    jurisdiction_reference: filing_receipt?.jurisdiction_reference || '',
    notes: filing_receipt?.notes || '',
  })
  const [filingDocument, setFilingDocument] = useState<File | null>(null)

  const signerParties = useMemo(() => parties.filter((party) => party.signer), [parties])
  const currentSignaturePacket = signature_packets.find((packet) => packet.status !== 'completed') || signature_packets[0]

  const resetMessages = () => {
    setMessage('')
    setError('')
  }

  const refresh = () => {
    window.location.reload()
  }

  const withHandler = async (operation: () => Promise<void>, successMessage: string) => {
    resetMessages()
    try {
      await operation()
      setMessage(successMessage)
      refresh()
    } catch (operationError) {
      setError(operationError instanceof Error ? operationError.message : 'Request failed')
    }
  }

  const onUpdateCase = (event: FormEvent) => {
    event.preventDefault()
    withHandler(async () => {
      await requestJson(casePath, {
        method: 'PATCH',
        data: {
          case: caseForm,
        },
      })
    }, 'Case updated')
  }

  const onUpdateSection = (event: FormEvent) => {
    event.preventDefault()
    withHandler(async () => {
      const payload = JSON.parse(sectionJson)
      await requestJson(`${casePath}/interview/${selectedSection}`, {
        method: 'PATCH',
        data: { section: payload },
      })
    }, 'Interview section saved')
  }

  const onAddParty = (event: FormEvent) => {
    event.preventDefault()
    withHandler(async () => {
      await requestJson(`${casePath}/parties`, {
        method: 'POST',
        data: {
          party: {
            role_name: partyForm.role_name,
            signer: partyForm.signer,
            appointed_on: partyForm.appointed_on || null,
            party_profile_id: partyForm.party_profile_id || null,
            profile_type: partyForm.profile_type,
            full_name: partyForm.full_name,
            legal_name: partyForm.legal_name,
          },
        },
      })
    }, 'Principal added')
  }

  const deleteParty = (partyId: string) => {
    withHandler(async () => {
      await requestJson(`${casePath}/parties/${partyId}`, { method: 'DELETE' })
    }, 'Principal removed')
  }

  const onAddOwnership = (event: FormEvent) => {
    event.preventDefault()
    withHandler(async () => {
      await requestJson(`${casePath}/ownership_edges`, {
        method: 'POST',
        data: {
          ownership_edge: {
            owner_party_profile_id: ownershipForm.owner_party_profile_id,
            subject_party_profile_id: ownershipForm.subject_party_profile_id,
            percentage_owned: ownershipForm.percentage_owned || null,
            control_type: ownershipForm.control_type || null,
          },
        },
      })
    }, 'Ownership relationship added')
  }

  const deleteOwnership = (edgeId: string) => {
    withHandler(async () => {
      await requestJson(`${casePath}/ownership_edges/${edgeId}`, { method: 'DELETE' })
    }, 'Ownership relationship removed')
  }

  const onUploadDocument = (event: FormEvent) => {
    event.preventDefault()
    withHandler(async () => {
      const formData = new FormData()
      formData.append('party_document[party_profile_id]', documentForm.party_profile_id)
      formData.append('party_document[document_type]', documentForm.document_type)
      if (documentForm.retention_until) formData.append('party_document[retention_until]', documentForm.retention_until)
      if (documentFiles) {
        Array.from(documentFiles).forEach((file) => {
          formData.append('party_document[documents][]', file)
        })
      }

      await requestJson(`${casePath}/party_documents`, {
        method: 'POST',
        data: formData,
      })
    }, 'Identity document uploaded')
  }

  const onUpdateEin = (event: FormEvent) => {
    event.preventDefault()
    withHandler(async () => {
      await requestJson(`${casePath}/ein`, {
        method: 'PATCH',
        data: {
          ein_application: {
            responsible_party_name: einForm.responsible_party_name,
            responsible_party_title: einForm.responsible_party_title,
            responsible_party_tax_id: einForm.responsible_party_tax_id,
            responsible_party_birth_date: einForm.responsible_party_birth_date,
            responsible_party_phone: einForm.responsible_party_phone,
            legal_structure: einForm.legal_structure,
            reason_for_applying: einForm.reason_for_applying,
          },
          submit: true,
        },
      })
    }, 'EIN application updated')
  }

  const createSignaturePacket = () => {
    withHandler(async () => {
      await requestJson(`${casePath}/signature_packets`, {
        method: 'POST',
        data: {
          signature_packet: {
            consent_version: 'v1',
          },
        },
      })
    }, 'Signature packet created')
  }

  const signForProfile = (profileId: string, signerName: string) => {
    if (!currentSignaturePacket) return

    withHandler(async () => {
      await requestJson(`${casePath}/signature_packets/${currentSignaturePacket.id}/signatures`, {
        method: 'POST',
        data: {
          signature: {
            signer_party_profile_id: profileId,
            signer_name: signerName,
            payload: {
              consent: true,
              typed_name: signerName,
            },
          },
        },
      })
    }, 'Signature captured')
  }

  const startCheckout = () => {
    withHandler(async () => {
      const response = await requestJson<{ checkout_url: string }>(`${casePath}/payments/checkout`, {
        method: 'POST',
        data: {
          currency: paymentForm.currency,
          service_fee_cents: Number(paymentForm.service_fee_cents),
          filing_fee_cents: Number(paymentForm.filing_fee_cents),
        },
      })

      if (response?.checkout_url) {
        window.location.href = response.checkout_url
      }
    }, 'Redirecting to checkout')
  }

  const generatePacket = () => {
    withHandler(async () => {
      await requestJson(`${casePath}/packet_versions`, {
        method: 'POST',
      })
    }, 'Packet generated')
  }

  const submitFilingReceipt = (event: FormEvent) => {
    event.preventDefault()

    withHandler(async () => {
      const formData = new FormData()
      formData.append('filing_receipt[submitted_at]', new Date(filingForm.submitted_at).toISOString())
      formData.append('filing_receipt[jurisdiction_reference]', filingForm.jurisdiction_reference)
      formData.append('filing_receipt[notes]', filingForm.notes)
      if (filingDocument) formData.append('filing_receipt[receipt_document]', filingDocument)

      await requestJson(`${casePath}/filing_receipt`, {
        method: filing_receipt ? 'PUT' : 'POST',
        data: formData,
      })
    }, 'Filing receipt saved')
  }

  return (
    <DashboardLayout
      title="US Registration Workflow"
      breadcrumbs={[
        { label: 'Business Registration', href: `/${accountSlug}/business-registration` },
        { label: 'US Cases', href: `/${accountSlug}/business-registration/us/cases` },
        { label: case_record.legal_name },
      ]}
    >
      <div className="space-y-6">
        {(message || error) && (
          <Card>
            <CardContent className="pt-6">
              {message && <p className="text-sm text-green-700">{message}</p>}
              {error && <p className="text-sm text-red-700">{error}</p>}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{case_record.legal_name}</CardTitle>
                <CardDescription>{case_record.state_code} • {case_record.entity_type_code} • {case_record.filing_type}</CardDescription>
              </div>
              <Badge>{case_record.lifecycle_state}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {case_record.readiness_errors.length === 0 ? (
              <p className="text-sm text-green-700">Case is readiness-complete.</p>
            ) : (
              <ul className="text-sm text-red-700 list-disc pl-5">
                {case_record.readiness_errors.map((readinessError) => (
                  <li key={readinessError}>{readinessError}</li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>1. Case Basics</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onUpdateCase} className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Legal Name</Label>
                <Input
                  value={caseForm.legal_name}
                  onChange={(event) => setCaseForm({ ...caseForm, legal_name: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Entity Type</Label>
                <Input
                  value={caseForm.entity_type_code}
                  onChange={(event) => setCaseForm({ ...caseForm, entity_type_code: event.target.value })}
                />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit">Save Case</Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>2. Jurisdiction Interview</CardTitle>
            <CardDescription>Legal terms are shown with plain-language aliases.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Section</Label>
                <select
                  className="w-full border rounded-md h-10 px-3"
                  value={selectedSection}
                  onChange={(event) => {
                    const sectionKey = event.target.value
                    setSelectedSection(sectionKey)
                    setSectionJson(JSON.stringify(case_record.interview_payload[sectionKey] || {}, null, 2))
                  }}
                >
                  {sectionKeys.map((sectionKey) => (
                    <option key={sectionKey} value={sectionKey}>
                      {section_schema[sectionKey]?.title || sectionKey}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Answer the questions below for this section. Required fields are marked with *.
              </p>
            </div>

            <form onSubmit={onUpdateSection} className="space-y-4">
              <div className="space-y-4">
                {section_schema[selectedSection]?.fields?.map((field: {key: string, label?: string, required?: boolean, type?: string}) => (
                  <div key={field.key} className="space-y-2">
                    <Label>
                      {field.label || field.key}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    <Input 
                      value={JSON.parse(sectionJson || '{}')[field.key] || ''}
                      onChange={(event) => {
                        const currentAnswers = JSON.parse(sectionJson || '{}')
                        const newAnswers = { ...currentAnswers, [field.key]: event.target.value }
                        setSectionJson(JSON.stringify(newAnswers, null, 2))
                      }}
                      placeholder={`Enter ${field.label || field.key}`}
                    />
                  </div>
                )) || (
                  <div className="space-y-2">
                    <Label>Section Answers (JSON format)</Label>
                    <textarea
                      className="w-full min-h-[180px] border rounded-md p-3 text-sm"
                      value={sectionJson}
                      onChange={(event) => setSectionJson(event.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      This section uses a custom format. Please enter valid JSON.
                    </p>
                  </div>
                )}
              </div>
              <Button type="submit">Save Section Answers</Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>3. Principals (Owners / Directors / Managers)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={onAddParty} className="grid md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Role</Label>
                <Input value={partyForm.role_name} onChange={(event) => setPartyForm({ ...partyForm, role_name: event.target.value })} placeholder="member, manager, director" required />
              </div>
              <div className="space-y-2">
                <Label>Appointed On</Label>
                <Input type="date" value={partyForm.appointed_on} onChange={(event) => setPartyForm({ ...partyForm, appointed_on: event.target.value })} />
              </div>

              <div className="space-y-2">
                <Label>Existing Profile (optional)</Label>
                <select
                  className="w-full border rounded-md h-10 px-3"
                  value={partyForm.party_profile_id}
                  onChange={(event) => setPartyForm({ ...partyForm, party_profile_id: event.target.value })}
                >
                  <option value="">Create new profile</option>
                  {party_profiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>{profile.display_name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label>Profile Type</Label>
                <select
                  className="w-full border rounded-md h-10 px-3"
                  value={partyForm.profile_type}
                  onChange={(event) => setPartyForm({ ...partyForm, profile_type: event.target.value as 'person' | 'entity' })}
                >
                  <option value="person">Person</option>
                  <option value="entity">Entity</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={partyForm.full_name} onChange={(event) => setPartyForm({ ...partyForm, full_name: event.target.value })} placeholder="Jane Owner" />
              </div>

              <div className="space-y-2">
                <Label>Legal Name</Label>
                <Input value={partyForm.legal_name} onChange={(event) => setPartyForm({ ...partyForm, legal_name: event.target.value })} placeholder="Acme Parent Corp" />
              </div>

              <div className="md:col-span-2 flex items-center gap-2">
                <input
                  id="party-signer"
                  type="checkbox"
                  checked={partyForm.signer}
                  onChange={(event) => setPartyForm({ ...partyForm, signer: event.target.checked })}
                />
                <Label htmlFor="party-signer">Require signer for this principal</Label>
              </div>

              <div className="md:col-span-2 flex justify-end">
                <Button type="submit">Add Principal</Button>
              </div>
            </form>

            <Separator />

            <div className="space-y-2">
              {parties.length === 0 ? (
                <p className="text-sm text-muted-foreground">No principals yet.</p>
              ) : parties.map((party) => (
                <div key={party.id} className="border rounded-md p-3 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">{party.party_profile.display_name}</p>
                    <p className="text-muted-foreground">{party.role_name} {party.signer ? '• Signer' : ''}</p>
                  </div>
                  <Button variant="outline" onClick={() => deleteParty(party.id)}>Remove</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>4. Ownership Graph</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={onAddOwnership} className="grid md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Owner</Label>
                <select className="w-full border rounded-md h-10 px-3" value={ownershipForm.owner_party_profile_id} onChange={(event) => setOwnershipForm({ ...ownershipForm, owner_party_profile_id: event.target.value })}>
                  <option value="">Select owner</option>
                  {party_profiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>{profile.display_name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Subject</Label>
                <select className="w-full border rounded-md h-10 px-3" value={ownershipForm.subject_party_profile_id} onChange={(event) => setOwnershipForm({ ...ownershipForm, subject_party_profile_id: event.target.value })}>
                  <option value="">Select subject</option>
                  {party_profiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>{profile.display_name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Percent Ownership</Label>
                <Input value={ownershipForm.percentage_owned} onChange={(event) => setOwnershipForm({ ...ownershipForm, percentage_owned: event.target.value })} placeholder="25.00" />
              </div>
              <div className="space-y-2">
                <Label>Control Type</Label>
                <Input value={ownershipForm.control_type} onChange={(event) => setOwnershipForm({ ...ownershipForm, control_type: event.target.value })} placeholder="direct, indirect, voting" />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit">Add Relationship</Button>
              </div>
            </form>

            <div className="space-y-2">
              {ownership_edges.length === 0 ? (
                <p className="text-sm text-muted-foreground">No ownership edges yet.</p>
              ) : ownership_edges.map((edge) => {
                const owner = party_profiles.find((profile) => profile.id === edge.owner_party_profile_id)
                const subject = party_profiles.find((profile) => profile.id === edge.subject_party_profile_id)

                return (
                  <div key={edge.id} className="border rounded-md p-3 flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium">{owner?.display_name} → {subject?.display_name}</p>
                      <p className="text-muted-foreground">{edge.percentage_owned || 'N/A'}% {edge.control_type || ''}</p>
                    </div>
                    <Button variant="outline" onClick={() => deleteOwnership(edge.id)}>Remove</Button>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>5. Identity Documents (Manual Verification)</CardTitle>
            <CardDescription>All principals require approved IDs before readiness.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={onUploadDocument} className="grid md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Principal</Label>
                <select className="w-full border rounded-md h-10 px-3" value={documentForm.party_profile_id} onChange={(event) => setDocumentForm({ ...documentForm, party_profile_id: event.target.value })}>
                  <option value="">Select principal</option>
                  {party_profiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>{profile.display_name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Document Type</Label>
                <Input value={documentForm.document_type} onChange={(event) => setDocumentForm({ ...documentForm, document_type: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Retention Until</Label>
                <Input type="date" value={documentForm.retention_until} onChange={(event) => setDocumentForm({ ...documentForm, retention_until: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Files</Label>
                <Input type="file" multiple onChange={(event) => setDocumentFiles(event.target.files)} />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit">Upload Document</Button>
              </div>
            </form>

            <div className="space-y-2">
              {party_documents.length === 0 ? (
                <p className="text-sm text-muted-foreground">No identity documents uploaded yet.</p>
              ) : party_documents.map((document) => (
                <div key={document.id} className="border rounded-md p-3 text-sm">
                  <p className="font-medium">{document.party_name} • {document.document_type}</p>
                  <p className="text-muted-foreground">Status: {document.verification_status} • Files: {document.file_count}</p>
                  {document.rejection_reason && <p className="text-red-700">Rejected: {document.rejection_reason}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>6. EIN</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onUpdateEin} className="grid md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Responsible Party Name</Label>
                <Input value={einForm.responsible_party_name} onChange={(event) => setEinForm({ ...einForm, responsible_party_name: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Responsible Party Title</Label>
                <Input value={einForm.responsible_party_title} onChange={(event) => setEinForm({ ...einForm, responsible_party_title: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Tax ID</Label>
                <Input value={einForm.responsible_party_tax_id} onChange={(event) => setEinForm({ ...einForm, responsible_party_tax_id: event.target.value })} placeholder={ein_application?.responsible_party_tax_id_masked || '###-##-####'} />
              </div>
              <div className="space-y-2">
                <Label>Birth Date</Label>
                <Input type="date" value={einForm.responsible_party_birth_date} onChange={(event) => setEinForm({ ...einForm, responsible_party_birth_date: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={einForm.responsible_party_phone} onChange={(event) => setEinForm({ ...einForm, responsible_party_phone: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Reason</Label>
                <Input value={einForm.reason_for_applying} onChange={(event) => setEinForm({ ...einForm, reason_for_applying: event.target.value })} />
              </div>
              <div className="md:col-span-2 bg-muted/30 rounded-lg p-4 text-sm space-y-2">
                <p className="font-medium text-muted-foreground">What happens next:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>We'll use this information to apply for your EIN with the IRS</li>
                  <li>The IRS typically processes EIN applications within 1-2 business days</li>
                  <li>You'll receive your EIN confirmation letter via email</li>
                </ul>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit">Save EIN Information</Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>7. In-house E-sign</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={createSignaturePacket}>Create Signature Packet</Button>

            <div className="space-y-2">
              {signature_packets.length === 0 ? (
                <p className="text-sm text-muted-foreground">No signature packets yet.</p>
              ) : (
                signature_packets.map((packet) => (
                  <div key={packet.id} className="border rounded-md p-3 space-y-2">
                    <p className="text-sm font-medium">Packet {packet.id.slice(0, 8)} • {packet.status}</p>
                    <div className="space-y-1">
                      {signerParties.map((party) => {
                        const signed = packet.signatures.find((signature) => signature.signer_party_profile_id === party.party_profile.id)

                        return (
                          <div key={party.id} className="flex items-center justify-between text-sm">
                            <span>{party.party_profile.display_name}</span>
                            {signed ? (
                              <Badge variant="outline">Signed</Badge>
                            ) : (
                              <Button
                                variant="outline"
                                onClick={() => signForProfile(party.party_profile.id, party.party_profile.display_name)}
                              >
                                Sign
                              </Button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>8. Payment (Stripe Checkout)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Input value={paymentForm.currency} disabled className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>Service Fee</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input 
                    value={(parseInt(paymentForm.service_fee_cents) / 100).toFixed(2)} 
                    disabled
                    className="bg-muted pl-7"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Our fee for preparing and filing your documents</p>
              </div>
              <div className="space-y-2">
                <Label>State Filing Fee</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input 
                    value={(parseInt(paymentForm.filing_fee_cents) / 100).toFixed(2)} 
                    disabled
                    className="bg-muted pl-7"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Required fee paid to {case_record.state_code}</p>
              </div>
            </div>
            
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">Service Fee</span>
                <span className="font-medium">${(parseInt(paymentForm.service_fee_cents) / 100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">State Filing Fee</span>
                <span className="font-medium">${(parseInt(paymentForm.filing_fee_cents) / 100).toFixed(2)}</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total</span>
                <span className="font-bold text-lg">
                  ${((parseInt(paymentForm.service_fee_cents) + parseInt(paymentForm.filing_fee_cents)) / 100).toFixed(2)}
                </span>
              </div>
            </div>
            
            <Button onClick={startCheckout} size="lg" className="w-full">
              Proceed to Payment
            </Button>

            {payment_order && (
              <div className="border rounded-md p-3 text-sm space-y-1">
                <p>Status: {payment_order.status}</p>
                <p>Subtotal: {payment_order.currency} {cents(payment_order.subtotal_cents)}</p>
                <p>Tax: {payment_order.currency} {cents(payment_order.tax_cents)}</p>
                <p>Total: {payment_order.currency} {cents(payment_order.total_cents)}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>9. Packet Generation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={generatePacket}>Generate Filing Packet</Button>
            <div className="space-y-2">
              {packet_versions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No packet versions yet.</p>
              ) : (
                packet_versions.map((packet) => (
                  <div key={packet.id} className="border rounded-md p-3 flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium">Packet {packet.id.slice(0, 8)} • {packet.status}</p>
                      <p className="text-muted-foreground">{packet.currency} {cents(packet.total_cents)} • {new Date(packet.generated_at).toLocaleString()}</p>
                    </div>
                    {packet.packet_pdf_attached && (
                      <div className="flex items-center gap-3">
                        <DocumentPreviewModal 
                          url={`${casePath}/packet_versions/${packet.id}/download`} 
                          filename={`Packet_${packet.id.slice(0, 8)}.pdf`}
                          trigger={
                            <button className="underline text-primary cursor-pointer text-sm">View PDF</button>
                          }
                        />
                        <a className="underline text-primary text-sm" href={`${casePath}/packet_versions/${packet.id}/download`}>Download</a>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>10. Remittance Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {remittance_tasks.length === 0 ? (
              <p className="text-sm text-muted-foreground">Remittance tasks will appear after successful payment.</p>
            ) : (
              remittance_tasks.map((task) => (
                <div key={task.id} className="border rounded-md p-3 text-sm">
                  <p className="font-medium">{task.jurisdiction_code} • {task.status}</p>
                  <p className="text-muted-foreground">{task.currency} {cents(task.amount_cents)}</p>
                  {task.reference_number && <p>Reference: {task.reference_number}</p>}
                  {task.failure_reason && <p className="text-red-700">Failure: {task.failure_reason}</p>}
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>11. Filing Receipt</CardTitle>
            <CardDescription>Submit official filing confirmation after remittance is complete.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submitFilingReceipt} className="grid md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Submitted At</Label>
                <Input type="datetime-local" value={filingForm.submitted_at} onChange={(event) => setFilingForm({ ...filingForm, submitted_at: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Jurisdiction Reference</Label>
                <Input value={filingForm.jurisdiction_reference} onChange={(event) => setFilingForm({ ...filingForm, jurisdiction_reference: event.target.value })} />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Notes</Label>
                <textarea className="w-full min-h-[100px] border rounded-md p-3 text-sm" value={filingForm.notes} onChange={(event) => setFilingForm({ ...filingForm, notes: event.target.value })} />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Receipt Document</Label>
                <Input type="file" onChange={(event) => setFilingDocument(event.target.files?.[0] || null)} />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit">Save Filing Receipt</Button>
              </div>
            </form>

            {filing_receipt && (
              <div className="mt-4 border rounded-md p-3 text-sm">
                <p className="font-medium">Filing receipt on record.</p>
                <p>Reference: {filing_receipt.jurisdiction_reference || 'N/A'}</p>
                <p>Submitted: {new Date(filing_receipt.submitted_at).toLocaleString()}</p>
                <p>Document attached: {filing_receipt.receipt_document_attached ? 'yes' : 'no'}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
